/**
 * Spacing and layout tokens following Apple's 8-point grid system
 * Provides consistent spacing, touch targets, and elevation system
 */

export const spacingTokens = {
  // Apple's 8-point grid system
  spacing: {
    0: 0,
    1: 4, // 0.25rem
    2: 8, // 0.5rem
    3: 12, // 0.75rem
    4: 16, // 1rem
    5: 20, // 1.25rem
    6: 24, // 1.5rem
    8: 32, // 2rem
    10: 40, // 2.5rem
    12: 48, // 3rem
    16: 64, // 4rem
    20: 80, // 5rem
    24: 96, // 6rem
    32: 128, // 8rem
  },

  // Touch target sizes (Apple HIG)
  touchTargets: {
    minimum: 44, // Minimum touch target
    comfortable: 48, // Comfortable touch target
    large: 56, // Large touch target
  },

  // Border radius inspired by Apple's design
  borderRadius: {
    none: 0,
    sm: 6, // 0.375rem - smoother small elements
    DEFAULT: 12, // 0.75rem - more rounded default
    md: 16, // 1rem - Apple/Nothing inspired
    lg: 20, // 1.25rem - premium feel
    xl: 28, // 1.75rem - smooth large cards
    '2xl': 20, // 1.25rem - moderate rounding
    full: 9999,
  },

  // Elevation system with shadow configurations
  elevation: {
    0: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    },
    1: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
      elevation: 1,
    },
    2: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 6,
      elevation: 2,
    },
    3: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 15,
      elevation: 3,
    },
    4: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.1,
      shadowRadius: 25,
      elevation: 4,
    },
    5: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 12 },
      shadowOpacity: 0.25,
      shadowRadius: 50,
      elevation: 5,
    },
  },
} as const;

// Type definitions for spacing tokens
export type Spacing = keyof typeof spacingTokens.spacing;
export type TouchTarget = keyof typeof spacingTokens.touchTargets;
export type BorderRadius = keyof typeof spacingTokens.borderRadius;
export type Elevation = keyof typeof spacingTokens.elevation;

// Utility types for layout
export interface SpacingConfig {
  margin?: Spacing;
  marginTop?: Spacing;
  marginRight?: Spacing;
  marginBottom?: Spacing;
  marginLeft?: Spacing;
  marginHorizontal?: Spacing;
  marginVertical?: Spacing;
  padding?: Spacing;
  paddingTop?: Spacing;
  paddingRight?: Spacing;
  paddingBottom?: Spacing;
  paddingLeft?: Spacing;
  paddingHorizontal?: Spacing;
  paddingVertical?: Spacing;
}

export interface ShadowConfig {
  elevation: Elevation;
}
